import { Injectable, OnDestroy } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { forkJoin, Observable, of, ReplaySubject, Subject } from 'rxjs';
import { filter, take, takeUntil } from 'rxjs/operators';
import { Alert } from '@shared/alert/models';
import { enumAlertType } from '@shared/alert/enums';

@Injectable({ providedIn: 'root' })
export class AlertService implements OnDestroy {
  private subject: Subject<Alert> = new Subject<Alert>();
  private keepAfterRouteChange: boolean = false;

  private destroy$: ReplaySubject<void> = new ReplaySubject<void>();

  constructor(private router: Router, private translateService: TranslateService) {
    router.events.pipe(takeUntil(this.destroy$)).subscribe((event) => {
      if (event instanceof NavigationStart) {
        if (this.keepAfterRouteChange) {
          this.keepAfterRouteChange = false;
        } else {
          this.clear();
        }
      }
    });
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
  }

  public getAlert(alertId?: string): Observable<any> {
    return this.subject.asObservable().pipe(filter((x: Alert) => x && x.alertId === alertId));
  }

  public info(message: string) {
    this.alert(new Alert({ message, type: enumAlertType.Info }));
  }

  public error(message?: string, title: string = 'LABELS.SOMETHING_DONT_WORK_TITLE', translateData?: Object): void {
    forkJoin({
      titleLg: this.translateService.get(title, translateData),
      messageLg: !message ? of(null) : this.translateService.get(message, translateData),
    })
      .pipe(take(1))
      .subscribe(({ titleLg, messageLg }) => {
        this.alert(new Alert({ title: titleLg, message: messageLg, type: enumAlertType.Error, keepAfterRouteChange: true }));
      });
  }

  public warning(message?: string, title: string = 'LABELS.SOMETHING_DONT_WORK_MESSAGE', translateData?: Object) {
    forkJoin({
      titleLg: !title ? of(null) : this.translateService.get(title, translateData),
      messageLg: !message ? of(null) : this.translateService.get(message, translateData),
    })
      .pipe(take(1))
      .subscribe(({ titleLg, messageLg }) => {
        this.alert(new Alert({ title: titleLg, message: messageLg, type: enumAlertType.Warning, keepAfterRouteChange: true }));
      });
  }

  public modalInfo(message: string, title: string = '', isCloseAfterTimeout: boolean = true) {
    this.translateService
      .get(message)
      .pipe(take(1))
      .subscribe((messageTranslated) => {
        this.alert(
          new Alert({ title, message: messageTranslated, type: enumAlertType.ModalInfo, keepAfterRouteChange: false, isCloseAfterTimeout }),
        );
      });
  }

  public modalError(message: string) {
    this.translateService
      .get(message)
      .pipe(take(1))
      .subscribe((messageTranslated) => {
        this.alert(
          new Alert({ title: messageTranslated, message: messageTranslated, type: enumAlertType.ModalError, keepAfterRouteChange: false }),
        );
      });
  }

  public modalSuccess(message: string, title: string = 'LABELS.SUCCESS'): void {
    this.alert(
      new Alert({
        title: this.translateService.instant(title),
        message: this.translateService.instant(message),
        type: enumAlertType.ModalSuccess,
        keepAfterRouteChange: false,
      }),
    );
  }

  public success(message?: string, title: string = 'LABELS.SUCCESS', translateData?: Object): void {
    forkJoin({
      titleLg: !title ? of(null) : this.translateService.get(title, translateData),
      messageLg: !message ? of(null) : this.translateService.get(message, translateData),
    })
      .pipe(take(1))
      .subscribe(({ titleLg, messageLg }) => {
        this.alert(new Alert({ title: titleLg, message: messageLg, type: enumAlertType.Success, keepAfterRouteChange: true }));
      });
  }

  private fatal(message: string) {
    this.error(message);
  }

  private critical(message: string) {
    this.error(message);
  }

  private alert(alert: Alert) {
    this.keepAfterRouteChange = alert.keepAfterRouteChange;
    this.subject.next(alert);
  }

  // clear alerts
  private clear(alertId?: string) {
    this.subject.next(new Alert({ alertId }));
  }
}
